import { createContext, useContext, type ReactNode } from 'react'
import { LayoutRenderer, type LayoutContextValue } from '../layout'
import type {
  CameraSelection,
  DestinationCamera,
  DestinationEvent,
  DestinationLiveStateSnapshot,
  DestinationMode,
  DestinationViewerSession,
} from './types'
import type { Layout } from '../layout'
import {
  CameraProvider,
  EventProvider,
  LiveContentProvider,
  ResultsProvider,
  TrackingProvider,
  ViewerSessionProvider,
  type ResultsProviderValue,
  type TrackingProviderSnapshot,
} from './providers'

export interface DestinationRuntimeState {
  mode: DestinationMode
  layoutRevision: number
}

const DestinationRuntimeContext = createContext<DestinationRuntimeState | null>(null)

export function useDestinationRuntime(): DestinationRuntimeState {
  const context = useContext(DestinationRuntimeContext)
  if (!context) {
    throw new Error('useDestinationRuntime must be used inside <DestinationRuntime>.')
  }
  return context
}

export interface DestinationRuntimeProps {
  mode: DestinationMode
  layout: Layout
  layoutRevision: number
  event: DestinationEvent
  cameras: DestinationCamera[]
  cameraSelection: CameraSelection
  viewerCount: number | null
  liveState: DestinationLiveStateSnapshot
  viewerSession: DestinationViewerSession
  tracking?: TrackingProviderSnapshot
  results?: ResultsProviderValue
  /**
   * Temporary compatibility bridge for v1 primitives. It disappears once all
   * components read only focused providers; media still remains above this
   * runtime boundary.
   */
  legacyLayoutContext: LayoutContextValue
  children?: ReactNode
}

export function DestinationRuntime({
  mode,
  layout,
  layoutRevision,
  event,
  cameras,
  cameraSelection,
  viewerCount,
  liveState,
  viewerSession,
  tracking,
  results,
  legacyLayoutContext,
  children,
}: DestinationRuntimeProps) {
  return (
    <DestinationRuntimeContext.Provider value={{ mode, layoutRevision }}>
      <EventProvider event={event}>
        <CameraProvider
          cameras={cameras}
          selection={cameraSelection}
          viewerCount={viewerCount}
        >
          <TrackingProvider snapshot={tracking}>
            <ResultsProvider value={results}>
              <LiveContentProvider snapshot={liveState}>
                <ViewerSessionProvider session={viewerSession}>
                  {children ?? (
                    <LayoutRenderer layout={layout} context={legacyLayoutContext} />
                  )}
                </ViewerSessionProvider>
              </LiveContentProvider>
            </ResultsProvider>
          </TrackingProvider>
        </CameraProvider>
      </EventProvider>
    </DestinationRuntimeContext.Provider>
  )
}
